import {useEffect, useState, type RefObject} from "react";
import type {Report} from "../../../../../data/models/Report";
import {PopupV5} from "../../../popup/PopupV5";
import {CreatePopupFieldStyled} from "../../../popup/CreatePopupField.styled";
import {GlobalSharingPopup, type ViewActionType} from "../../../sharing/GlobalSharingPopup";
import {ViewPreferenceCategory, XyiconFeature} from "../../../../../generated/api/base";
import {useAppStore} from "../../../../../StateManager";
import {notify} from "../../../../../utils/Notify";
import {NotificationType} from "../../../popup/NotificationV5";
import {ReportScope} from "../../../../../generated/api/reports";
import {createGlobalView} from "../../../../../data/models/ViewUtils";
import {ReportWizardV5} from "./wizard/ReportWizardV5";

interface IReportPanelProps {
	readonly onRunReport: (report: Report) => void;
	readonly report: Report;
	readonly onClose: (saveChanges: boolean, report: Report) => void;
	readonly parentRef?: RefObject<HTMLDivElement>;
	readonly isReportView?: boolean;
	readonly favoriteViewFolderIdToSaveNewViewInto?: string;
	readonly viewFolderIdToSaveNewViewInto?: string;
}

export const ReportPanelV5 = (props: IReportPanelProps) => {
	const {report, parentRef, isReportView, onClose, onRunReport, favoriteViewFolderIdToSaveNewViewInto, viewFolderIdToSaveNewViewInto} = props;
	const appState = useAppStore((state) => state.appState);
	const transport = appState.app.transport;
	const [reportActionType, setReportActionType] = useState<ViewActionType>("create");
	const [isGlobalActionPanelOpen, setIsGlobalActionPanelOpen] = useState<boolean>(false);
	const [confirmationType, setConfirmationType] = useState<"save" | "saveAndRun">("save");
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const applyCreatedReport = async (createdReport: Report) => {
		if (report.isGlobal && !createdReport.isGlobal) {
			// request to reports/markasglobal endpoint is needed, because the create request always returns with isGlobal: false
			await transport.services.feature.markViewAsGlobal(createdReport, !!viewFolderIdToSaveNewViewInto);
		}

		if (!isReportView) {
			return;
		}

		if (!appState.actions.getReportViewById(createdReport.id)) {
			appState.reportViews.push(createdReport);
		}

		appState.selectedReportViewId = createdReport.id;
		const newView = appState.actions.getReportViewById(appState.selectedReportViewId);

		if (favoriteViewFolderIdToSaveNewViewInto) {
			await newView.setFavorite(true, favoriteViewFolderIdToSaveNewViewInto);
		}

		if (newView?.isGlobal && appState.organization && viewFolderIdToSaveNewViewInto) {
			await createGlobalView(newView.id, ViewPreferenceCategory.Report, appState, viewFolderIdToSaveNewViewInto);
		}

		if (!report.isDuplicate) {
			window.dispatchEvent(new Event("newReportViewCreated"));
		}
	};

	const onCloseGlobalSharingPopup = (saveChanges: boolean) => {
		setIsGlobalActionPanelOpen(false);
		if (saveChanges) {
			onClose(true, report);
		}
	};

	const onSaveClick = async () => {
		if (!validateReport()) {
			return;
		}

		setIsSaving(true);

		if (report.id) {
			await transport.services.feature.updateReport(report);
			setIsSaving(false);
			onClose(true, report);
		} else {
			const reports = await transport.services.feature.create<Report>(report.serializeData(), XyiconFeature.Report);
			const firstReport = reports[0];

			if (firstReport) {
				await applyCreatedReport(firstReport);
			}

			setIsSaving(false);
			onClose(true, firstReport ?? report);
		}
	};

	const onSaveAndRunClick = async () => {
		if (!validateReport()) {
			return;
		}

		setIsSaving(true);

		if (report.id) {
			await transport.services.feature.updateReport(report);
			setIsSaving(false);
			onClose(true, report);
			onRunReport(report);
		} else {
			const reports = await transport.services.feature.create<Report>(report.serializeData(), XyiconFeature.Report);
			const firstReport = reports[0];

			if (firstReport) {
				await applyCreatedReport(firstReport);
				setIsSaving(false);

				if (isReportView) {
					onClose(true, firstReport);

					return;
				}

				onClose(true, firstReport);
				onRunReport(firstReport);
			} else {
				setIsSaving(false);
			}
		}
	};

	const handleSaveClick = (type: "save" | "saveAndRun") => {
		if (report.isGlobal && !report.isDuplicate) {
			setIsGlobalActionPanelOpen(true);
			setConfirmationType(type);
		} else {
			type === "save" ? onSaveClick() : onSaveAndRunClick();
		}
	};

	const validateReport = (): boolean => {
		let errorMessage = "";

		if (!report.name) {
			errorMessage = "Please specify a name for your report!";
		}
		if (report.scope === ReportScope.PortfolioIDList && report.portfolioIDList?.length < 1) {
			errorMessage = "Portfolio list cannot be empty!";
		}

		if (errorMessage) {
			notify(appState.app.notificationContainer, {
				type: NotificationType.Error,
				title: "Warning",
				description: errorMessage,
				lifeTime: Infinity,
			});
		}

		return !errorMessage;
	};

	const getTitle = () => {
		const isReportViewAndNameMaybe = `${isReportView ? "View " : ""}${report.name ? ` - ${report.name}` : ""}`;

		if (report.id) {
			return `Edit${report.isGlobal ? " Global" : ""} Report ${isReportViewAndNameMaybe}`;
		} else if (report.isDuplicate) {
			return `Duplicate Report ${isReportViewAndNameMaybe}`;
		} else {
			return `Create Report ${isReportViewAndNameMaybe}`;
		}
	};

	useEffect(() => {
		if (report.id) {
			setReportActionType("modify");
		} else if (!report.isDuplicate) {
			setReportActionType("create");
		}
	}, [report.id, report.isDuplicate]);

	return isGlobalActionPanelOpen ? (
		<GlobalSharingPopup
			viewItems={[report]}
			onClose={onCloseGlobalSharingPopup}
			viewActionType={reportActionType}
			isReports={true}
			onConfirm={confirmationType === "save" ? onSaveClick : onSaveAndRunClick}
		/>
	) : (
		<PopupV5
			onClose={() => onClose(false, report)}
			label={getTitle()}
			className="ReportCreation"
			freezeRoot={true}
			centerOnScreen={true}
			width="60%"
			height="80vh"
			parentRef={parentRef?.current}
			closeOnClickOutside={false}
		>
			<CreatePopupFieldStyled>
				<ReportWizardV5
					report={report}
					onClose={(saveChanges: boolean) => onClose(saveChanges, report)}
					handleSaveClick={handleSaveClick}
					isSaving={isSaving}
					isReportView={isReportView}
				/>
			</CreatePopupFieldStyled>
		</PopupV5>
	);
};
